import { Scaling } from 'redux/mode/reducers';

export interface Size {
	width: number;
	height: number;
}

export interface Frame {
	width: number;
	height: number;
}

export interface Viewport {
	x: number;
	y: number;
	width: number;
	height: number;
	scale: number; // how much iframe content is scaled up/down
}

const MIN_FRAME_SPACING = 20;

const calculateRenderedDimensions = (
	effectiveWidth: number,
	frameHeight: number,
	scale: number
) => ({
	renderedWidth: effectiveWidth * scale,
	renderedHeight: frameHeight * scale,
});

const centerInContainer = (renderedWidth: number, renderedHeight: number, containerSize: Size) => ({
	x: Math.max(0, (containerSize.width - renderedWidth) / 2),
	y: Math.max(0, (containerSize.height - renderedHeight) / 2),
});

const calculateMockupScale = (
	frame: Frame,
	containerSize: Size,
	effectiveWidth: number
): number => {
	const scaleX = containerSize.width / effectiveWidth;
	const scaleY = (containerSize.height - MIN_FRAME_SPACING * 2) / frame.height;
	return Math.min(scaleX, scaleY);
};

// Main function
export const computeViewport = (
	frameSize: Size,
	containerSize: Size,
	scaling: Scaling,
	fullscreenMode: boolean = false
): Viewport => {
	// Calculate scale based on scaling mode (same for both fullscreen and normal)
	const scale =
		scaling === 'mockup'
			? calculateMockupScale(
					{ width: frameSize.width, height: frameSize.height },
					containerSize,
					frameSize.width
			  )
			: 1.0;

	//---[ fullscreen ]---------------------
	if (fullscreenMode) {
		if (scaling === 'mockup') {
			return {
				x: 0,
				y: 0,
				width: containerSize.width / scale,
				height: containerSize.height / scale,
				scale,
			};
		} else {
			// `actual-size`
			return {
				x: 0,
				y: 0,
				width: containerSize.width,
				height: containerSize.height,
				scale,
			};
		}
	}

	//---[ non-fullscreen ]---------------------
	const { renderedWidth, renderedHeight } = calculateRenderedDimensions(
		frameSize.width,
		frameSize.height,
		scale
	);
	const { x, y } = centerInContainer(renderedWidth, renderedHeight, containerSize);
	if (scaling === 'mockup') {
		return {
			x,
			y,
			width: frameSize.width,
			height: frameSize.height,
			scale,
		};
	}
	// `actual-size`
	return {
		x,
		y,
		width: frameSize.width,
		height: frameSize.height,
		scale,
	};
};
